import useTranslate from "../../../../modules/local/useTranslate";
import React, {useMemo, useState} from "react";
import {notifyOnError} from "../../factory/createErrorEvent";
import useAsyncAction from "../../../../modules/asyncCache/useAsyncAction";
import {
    course_class_createGrade_Api,
    course_class_deleteGrade_Api,
    course_class_editGrade_Api,
    course_class_exam_update_Api
} from "../../../../apis";
import {getResponseItem, NullAsDefault} from "../../../Shared";
import {NotificationActionTypes, successNotify} from "../../factory/createNotification";
import {showConfirmDialog, showDeleteConfirmDialog} from "../../factory/createConfirmDialog";
import {createModalTitle} from "../../../../components/renderFunctions/createModalTitle";
import {getId} from "../../../../apis/model/base";
import {FullSideModalLayout} from "../../../../components/Modal/SideModalLayout";
import {Modal} from "antd";
import {FetchCourseClassExamById, FetchGradeById} from "../components/FetchEntity";
import LoadingBlock from "../../../Loading/LoadingBlock";
import {GradeActionTypes} from "../index";
import {
    CourseClassGradeCreateForm,
    CourseClassGradesForm,
    CourseClassGradeUpdateForm,
    CourseClassStudentGradeStats
} from "../CourseClass/CourseClassGradeForm";
import {CourseClassGradeHistory} from "../CourseClass/CourseClassGradeHistory";
import {CourseClassExamUpdateForm} from "../CourseClass/CourseClassExamForm";


const GradeUpdateForm = ({grade_id, ...props}) => (
    <FetchGradeById id={grade_id}>
        {({item, isLoading}) => {
            if (isLoading) {
                return <LoadingBlock/>
            }
            return (
                <CourseClassGradeUpdateForm
                    {...props}
                    initialValues={item}
                />
            )
        }}
    </FetchGradeById>
)

const CourseClassExamUpdateFormWrapper = ({course_class_exam_id, ...props}) => (
    <FetchCourseClassExamById id={course_class_exam_id}>
        {({item, isLoading}) => {
            if (isLoading) {
                return <LoadingBlock/>
            }
            return (
                <CourseClassExamUpdateForm
                    {...props}
                    initialValues={item}
                />
            )
        }}
    </FetchCourseClassExamById>
)


export const useCourseGrades = ({course_class}) => {
    const t = useTranslate()

    const [refresh_token, set_refresh_token] = useState()

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const onCancel = () => {
        setAction({})
    }

    const onRefresh = () => {
        onCancel()
        set_refresh_token(Date.now())
    }

    const onError = notifyOnError(t)

    const {handleAsyncAction: onCreate, ...creatingAsync} = useAsyncAction({
        onError,
        apiInfo: course_class_createGrade_Api,
        onSuccess: (result, data) => {
            const new_attendant = getResponseItem(data?.response)
            successNotify(NotificationActionTypes.CREATE, t)
            onRefresh()
            // Nếu vừa tạo grade có gắn exam → mở thẳng form khai báo bài thi
            if (new_attendant && new_attendant.course_class_exam && new_attendant.course_class_exam.id) {
                setAction({type: GradeActionTypes.COURSE_CLASS_EXAM_UPDATE, value: new_attendant.course_class_exam})
            } else {
                setAction({type: GradeActionTypes.GRADE_DETAIL, value: new_attendant})
            }
        },
    })

    const {handleAsyncAction: onUpdate, ...updatingAsync} = useAsyncAction({
        onError,
        apiInfo: course_class_editGrade_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onRefresh()
        },
    })

    const {handleAsyncAction: onCourseClassExamUpdate, ...updatingCourseClassExamAsync} = useAsyncAction({
        onError,
        apiInfo: course_class_exam_update_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.UPDATE, t)
            onRefresh()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        onError,
        apiInfo: course_class_deleteGrade_Api,
        onSuccess: (result, data) => {
            successNotify(NotificationActionTypes.DELETE, t)
            onRefresh()
        },
    })

    const handleAction = (action, params) => {
        switch (action) {
            case GradeActionTypes.VIEW_BY_SUBJECT_FILTER:
            case GradeActionTypes.GRADE_DETAIL:
            case GradeActionTypes.GRADE_HISTORY:
            case GradeActionTypes.GRADE_CREATE:
            case GradeActionTypes.GRADE_UPDATE:
            case GradeActionTypes.COURSE_CLASS_EXAM_UPDATE:
                setAction({
                    type: action,
                    value: params,
                })
                break
            case GradeActionTypes.GRADE_DELETE:
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete grades',
                    message: 'are you sure you want to delete grades',
                    onOk: () => {
                        onDelete({}, {id: params?.id})
                    },
                })
                break
            case GradeActionTypes.GRADE_ADD_PARTICIPANT:
                showConfirmDialog({
                    translate: t,
                    title: 'sync students',
                    message: 'Are you sure you want to sync the class student list? The test participants list will be updated accordingly.',
                    onOk: () => {
                        onUpdate({sync_students: 1}, {id: params?.id})
                    },
                })
                break
            default:
                break
        }
    }

    const modal = useMemo(() => {
        let params = {}
        let modal_params = {}
        let Component = NullAsDefault
        switch (action?.type) {
            case GradeActionTypes.GRADE_CREATE:
                modal_params = {
                    title: createModalTitle('create a grade sheet'),
                    width: 600,
                }
                params = {
                    isSubmitting: creatingAsync.isLoading,
                    onSubmit: (values) => {
                        onCreate(values, {id: getId(course_class)})
                    },
                }
                Component = CourseClassGradeCreateForm
                break
            case GradeActionTypes.GRADE_UPDATE:
                const grade_id = getId(action?.value)
                modal_params = {
                    title: createModalTitle('update a grade sheet'),
                    width: 600,
                }
                params = {
                    grade_id,
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onUpdate(values, {id: grade_id})
                    },
                }
                Component = GradeUpdateForm
                break
            case GradeActionTypes.COURSE_CLASS_EXAM_UPDATE:
                const course_class_exam = action?.value
                const course_class_exam_id = getId(course_class_exam)
                modal_params = {
                    title: createModalTitle('edit exam'),
                    width: 800,
                }
                params = {
                    course_class_exam,
                    course_class_exam_id,
                    isSubmitting: updatingAsync.isLoading,
                    onSubmit: (values) => {
                        onCourseClassExamUpdate(values, {id: course_class_exam_id})
                    },
                }
                Component = CourseClassExamUpdateFormWrapper
                break
            case GradeActionTypes.GRADE_DETAIL:
                return (
                    <FullSideModalLayout
                        right={true}
                        onCancel={onCancel}
                        title={createModalTitle('view grades')}>
                        <CourseClassGradesForm
                            item={course_class}
                            grade={action?.value}
                        />
                    </FullSideModalLayout>
                )
            case GradeActionTypes.GRADE_HISTORY:
                return (
                    <FullSideModalLayout
                        right={true}
                        onCancel={onCancel}
                        title={createModalTitle('history')}>
                        <CourseClassGradeHistory
                            item={course_class}
                            grade={action?.value}
                        />
                    </FullSideModalLayout>
                )
            case GradeActionTypes.VIEW_BY_SUBJECT_FILTER:
                return (
                    <FullSideModalLayout
                        right={true}
                        onCancel={onCancel}
                        title={createModalTitle('grade insights')}>
                        <CourseClassStudentGradeStats
                            courseClass={course_class}
                        />
                    </FullSideModalLayout>
                )
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                destroyOnHidden={true}
                open={action?.type}
                className="custom-modal"
                {...modal_params}>
                <Component
                    {...{onCancel}}
                    {...params}
                    course_class={course_class}
                />
            </Modal>
        )
    }, [action, course_class, creatingAsync.isLoading, updatingAsync.isLoading])

    return {
        modal,
        action,
        handleAction,
        refresh_token,
    }
}